import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Trash2,
  Edit2,
  Edit3,
  Save as SaveIcon,
  X,
  Package,
  Wrench,
  BookOpen,
  Image as ImageIcon,
  ImagePlus,
  Upload,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BrandsAndProducts — Phase 1 MVP.
 *
 * List / create / view / edit / delete brands. Extraction pipeline,
 * products, services, assets, and AI Knowledge Base come in Phase 2-4.
 * Selecting a brand opens a detail pane with placeholder tabs so users
 * can see the intended shape today.
 */
export default function BrandsAndProducts() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/brands`, {
        withCredentials: true,
      });
      setBrands(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error("Failed to load brands:", err);
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  };

  const loadBrand = async (brandId) => {
    setSelectedLoading(true);
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/brands/${brandId}`, {
        withCredentials: true,
      });
      setSelectedBrand(resp.data);
      setEditForm({
        name: resp.data.name || "",
        description: resp.data.description || "",
      });
    } catch (err) {
      console.error("Failed to load brand:", err);
      toast.error("Failed to load brand");
      setSelectedBrandId(null);
    } finally {
      setSelectedLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, []);

  useEffect(() => {
    if (selectedBrandId) {
      loadBrand(selectedBrandId);
    } else {
      setSelectedBrand(null);
      setEditing(false);
    }
  }, [selectedBrandId]);

  const handleCreate = async () => {
    const name = createForm.name.trim();
    if (!name) {
      toast.error("Brand name is required");
      return;
    }
    setCreating(true);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/brands`,
        { name, description: createForm.description.trim() || undefined },
        { withCredentials: true }
      );
      toast.success(`Brand "${resp.data.name}" created`);
      setCreateDialog(false);
      setCreateForm({ name: "", description: "" });
      await loadBrands();
      // Auto-open the newly-created brand so the user immediately sees
      // the (still-empty) profile page and understands the next steps.
      setSelectedBrandId(resp.data.id);
    } catch (err) {
      console.error("Create brand failed:", err);
      toast.error(err.response?.data?.detail || "Failed to create brand");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedBrand) return;
    const name = editForm.name.trim();
    if (!name) {
      toast.error("Brand name is required");
      return;
    }
    setSaving(true);
    try {
      const resp = await axios.put(
        `${BACKEND_URL}/api/brands/${selectedBrand.id}`,
        { name, description: editForm.description.trim() },
        { withCredentials: true }
      );
      setSelectedBrand(resp.data);
      setEditing(false);
      toast.success("Brand updated");
      await loadBrands();
    } catch (err) {
      console.error("Update brand failed:", err);
      toast.error(err.response?.data?.detail || "Failed to update brand");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (brandId, brandName) => {
    if (!window.confirm(`Delete brand "${brandName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/brands/${brandId}`, {
        withCredentials: true,
      });
      toast.success(`Brand "${brandName}" deleted`);
      if (selectedBrandId === brandId) setSelectedBrandId(null);
      await loadBrands();
    } catch (err) {
      console.error("Delete brand failed:", err);
      toast.error(err.response?.data?.detail || "Failed to delete brand");
    }
  };

  // ── Detail view ──────────────────────────────────────────────────────
  if (selectedBrandId) {
    return (
      <div className="space-y-6" data-testid="brand-detail-view">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedBrandId(null)}
            data-testid="brand-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to brands
          </Button>
        </div>

        {selectedLoading || !selectedBrand ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center text-gray-500">
              Loading brand…
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  {editing ? (
                    <div className="flex-1 space-y-3">
                      <Input
                        value={editForm.name}
                        onChange={(e) =>
                          setEditForm({ ...editForm, name: e.target.value })
                        }
                        placeholder="Brand name"
                        className="text-xl font-semibold"
                        data-testid="brand-edit-name"
                      />
                      <Textarea
                        value={editForm.description}
                        onChange={(e) =>
                          setEditForm({ ...editForm, description: e.target.value })
                        }
                        placeholder="Short brand description (optional)"
                        rows={3}
                        data-testid="brand-edit-description"
                      />
                    </div>
                  ) : (
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-2xl">
                          {selectedBrand.name}
                        </CardTitle>
                        <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">
                          {selectedBrand.description || (
                            <span className="italic text-gray-400">
                              No description yet — click Edit to add one.
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 flex-shrink-0">
                    {editing ? (
                      <>
                        <Button
                          onClick={handleSaveEdit}
                          disabled={saving}
                          size="sm"
                          data-testid="brand-save-btn"
                        >
                          <SaveIcon className="w-4 h-4 mr-1" />
                          {saving ? "Saving…" : "Save"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditing(false);
                            setEditForm({
                              name: selectedBrand.name || "",
                              description: selectedBrand.description || "",
                            });
                          }}
                          data-testid="brand-cancel-edit-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(true)}
                          data-testid="brand-edit-btn"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:bg-red-50"
                          onClick={() =>
                            handleDelete(selectedBrand.id, selectedBrand.name)
                          }
                          data-testid="brand-delete-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Placeholder tabs — extraction pipeline fills these in Phase 2-4 */}
            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="grid grid-cols-5 w-full max-w-2xl">
                <TabsTrigger value="profile" data-testid="tab-profile">
                  Profile
                </TabsTrigger>
                <TabsTrigger value="assets" data-testid="tab-assets">
                  <ImageIcon className="w-4 h-4 mr-1" />
                  Assets
                </TabsTrigger>
                <TabsTrigger value="products" data-testid="tab-products">
                  <Package className="w-4 h-4 mr-1" />
                  Products
                </TabsTrigger>
                <TabsTrigger value="services" data-testid="tab-services">
                  <Wrench className="w-4 h-4 mr-1" />
                  Services
                </TabsTrigger>
                <TabsTrigger value="kb" data-testid="tab-kb">
                  <BookOpen className="w-4 h-4 mr-1" />
                  Knowledge Base
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-4">
                <BrandProfileTab
                  brand={selectedBrand}
                  onBrandUpdate={(updated) => setSelectedBrand(updated)}
                />
              </TabsContent>

              <TabsContent value="assets" className="mt-4">
                <BrandAssetsTab
                  brand={selectedBrand}
                  onBrandUpdate={(updated) => setSelectedBrand(updated)}
                />
              </TabsContent>

              <TabsContent value="products" className="mt-4">
                <BrandChildrenTab
                  key={`products-${selectedBrand.id}`}
                  brand={selectedBrand}
                  kind="products"
                  singular="product"
                  Icon={Package}
                />
              </TabsContent>

              <TabsContent value="services" className="mt-4">
                <BrandChildrenTab
                  key={`services-${selectedBrand.id}`}
                  brand={selectedBrand}
                  kind="services"
                  singular="service"
                  Icon={Wrench}
                />
              </TabsContent>

              <TabsContent value="kb" className="mt-4">
                <PlaceholderCard
                  title="AI Knowledge Base"
                  message="Structured knowledge repository consumed by Ad Creator, Studio Pro, Movie Studio, etc. Ensures every generated concept, script, and asset stays on-brand."
                  cta="Coming in Phase 4 — Cross-app integration"
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="brands-list-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Brands &amp; Products</h1>
          <p className="text-gray-600">
            Centralized brand knowledge base — used across every LoopAi app
          </p>
        </div>
        <Button
          onClick={() => setCreateDialog(true)}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          data-testid="brands-create-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Brand
        </Button>
      </div>

      <Card className="border-0 shadow-lg">
        <CardContent className="p-8">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading brands…</div>
          ) : brands.length === 0 ? (
            <div className="text-center py-12" data-testid="brands-empty-state">
              <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No brands yet
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Add your first brand to start building a shared knowledge base
                that every LoopAi app can use.
              </p>
              <Button
                onClick={() => setCreateDialog(true)}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="brands-create-first-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Brand
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {brands.map((brand) => (
                <Card
                  key={brand.id}
                  className="border-2 hover:border-indigo-400 hover:shadow-lg transition-all duration-200 cursor-pointer"
                  data-testid={`brand-card-${brand.id}`}
                  onClick={() => setSelectedBrandId(brand.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg truncate">
                          {brand.name}
                        </CardTitle>
                        <p className="text-xs text-gray-500 mt-1">
                          Created{" "}
                          {new Date(brand.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:bg-red-50 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(brand.id, brand.name);
                        }}
                        data-testid={`brand-card-delete-${brand.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem]">
                      {brand.description || (
                        <span className="italic text-gray-400">
                          No description
                        </span>
                      )}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">
                Brand name <span className="text-red-500">*</span>
              </label>
              <Input
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm({ ...createForm, name: e.target.value })
                }
                placeholder="e.g. VIRIYA"
                maxLength={200}
                autoFocus
                data-testid="brands-create-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Short description (optional)
              </label>
              <Textarea
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm({ ...createForm, description: e.target.value })
                }
                placeholder="One-liner about what this brand does. Extraction (Phase 2) will fill in the rest."
                rows={4}
                maxLength={2000}
                data-testid="brands-create-description-input"
              />
              <div className="text-xs text-gray-400 mt-1">
                {createForm.description.length} / 2000
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCreateDialog(false)}
              disabled={creating}
              data-testid="brands-create-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !createForm.name.trim()}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              data-testid="brands-create-submit-btn"
            >
              {creating ? "Creating…" : "Create Brand"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PlaceholderCard = ({ title, message, cta }) => (
  <Card className="border-2 border-dashed border-gray-300 bg-gray-50/60">
    <CardContent className="p-8 text-center">
      <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 max-w-lg mx-auto mb-4">{message}</p>
      <span className="inline-block px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
        {cta}
      </span>
    </CardContent>
  </Card>
);

// ─── Brand Profile tab (Phase 2 extraction pipeline) ────────────────────────

const COST_WEBSITE = 5;
const COST_PER_SOCIAL = 10;
const COST_PER_DOC = 3;
const COST_CAP = 50;
const ALLOWED_DOC_EXT = [".pdf", ".docx", ".doc", ".ppt", ".pptx", ".txt", ".md", ".csv", ".rtf"];
const MAX_DOC_BYTES = 10 * 1024 * 1024;
const MAX_SOCIALS = 8;

function computeCost({ hasWebsite, socialCount, docCount }) {
  let c = 0;
  if (hasWebsite) c += COST_WEBSITE;
  c += socialCount * COST_PER_SOCIAL;
  c += docCount * COST_PER_DOC;
  return Math.min(c, COST_CAP);
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // "data:<mime>;base64,<...>"
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const BrandProfileTab = ({ brand, onBrandUpdate }) => {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [socialUrls, setSocialUrls] = useState([""]);
  const [textDescription, setTextDescription] = useState("");
  const [docs, setDocs] = useState([]); // [{name, mime_type, size_bytes, data}]
  const [running, setRunning] = useState(false);
  const [jobStatus, setJobStatus] = useState(null); // "processing" | "completed" | "failed"
  const [errorMsg, setErrorMsg] = useState(null);
  const pollRef = React.useRef(null);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const socialCount = socialUrls.filter((s) => s.trim()).length;
  const docCount = docs.length;
  const hasWebsite = websiteUrl.trim().length > 0;
  const anySource = hasWebsite || socialCount > 0 || textDescription.trim().length > 0 || docCount > 0;
  const estCost = computeCost({ hasWebsite, socialCount, docCount });

  const handleAddSocial = () => {
    if (socialUrls.length < MAX_SOCIALS) setSocialUrls([...socialUrls, ""]);
  };
  const handleRemoveSocial = (i) => {
    setSocialUrls(socialUrls.filter((_, idx) => idx !== i));
  };
  const handleSocialChange = (i, val) => {
    const next = [...socialUrls];
    next[i] = val;
    setSocialUrls(next);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (docs.length + files.length > 10) {
      toast.error(`Max 10 documents (already have ${docs.length})`);
      return;
    }
    const oversized = files.find((f) => f.size > MAX_DOC_BYTES);
    if (oversized) {
      toast.error(`"${oversized.name}" exceeds 10 MB limit`);
      return;
    }
    try {
      const encoded = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          mime_type: f.type || "application/octet-stream",
          size_bytes: f.size,
          data: await fileToBase64(f),
        }))
      );
      setDocs((prev) => [...prev, ...encoded]);
    } catch (err) {
      toast.error("Failed to read file(s)");
    }
    e.target.value = ""; // reset input so same file can be re-picked
  };

  const removeDoc = (idx) => setDocs(docs.filter((_, i) => i !== idx));

  const beginPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setJobStatus("processing");
    const start = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000; // 5 min
    pollRef.current = setInterval(async () => {
      if (Date.now() - start > TIMEOUT_MS) {
        clearInterval(pollRef.current);
        setJobStatus("failed");
        setRunning(false);
        setErrorMsg("Extraction timed out after 5 minutes. Please try again.");
        return;
      }
      try {
        const r = await axios.get(`${BACKEND_URL}/api/ai/jobs/${jobId}`, {
          withCredentials: true,
        });
        const st = r.data?.status;
        if (st === "completed") {
          clearInterval(pollRef.current);
          setJobStatus("completed");
          setRunning(false);
          // Refresh the brand doc so onBrandUpdate gets the new profile
          try {
            const b = await axios.get(`${BACKEND_URL}/api/brands/${brand.id}`, {
              withCredentials: true,
            });
            onBrandUpdate?.(b.data);
            toast.success("Brand profile extracted!");
            setWizardOpen(false);
          } catch (_) {
            /* ignore */
          }
        } else if (st === "failed") {
          clearInterval(pollRef.current);
          setJobStatus("failed");
          setRunning(false);
          setErrorMsg(r.data?.error || "Extraction failed");
        }
      } catch (err) {
        // transient — keep polling
      }
    }, 3000);
  };

  const handleStart = async () => {
    setErrorMsg(null);
    setRunning(true);
    try {
      const body = {
        website_url: websiteUrl.trim() || undefined,
        social_urls: socialUrls.filter((s) => s.trim()),
        text_description: textDescription.trim() || undefined,
        documents: docs,
      };
      const resp = await axios.post(
        `${BACKEND_URL}/api/brands/${brand.id}/extract`,
        body,
        { withCredentials: true }
      );
      toast.info(`Extraction queued — cost ${resp.data.credits_cost} credits`);
      beginPolling(resp.data.job_id);
    } catch (err) {
      setRunning(false);
      const detail = err.response?.data?.detail || err.response?.data?.error_code || err.message;
      setErrorMsg(String(detail));
      toast.error(String(detail));
    }
  };

  const profile = brand?.profile;
  const hasProfile = profile && typeof profile === "object" && Object.keys(profile).length > 0;

  return (
    <div className="space-y-4">
      {/* ── Header row: current state summary + action button ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Brand Profile</h3>
          <p className="text-sm text-gray-500">
            {hasProfile
              ? "Auto-extracted from your sources. Re-run any time to refresh."
              : "Add sources below and we'll build a full brand profile."}
          </p>
        </div>
        {!wizardOpen && (
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="brand-extract-open-wizard"
          >
            <Plus className="w-4 h-4 mr-2" />
            {hasProfile ? "Re-extract" : "Extract Brand Info"}
          </Button>
        )}
      </div>

      {/* ── Extraction wizard ── */}
      {wizardOpen && (
        <Card className="border-2 border-indigo-200">
          <CardHeader>
            <CardTitle className="text-base">Add sources</CardTitle>
            <p className="text-xs text-gray-500">
              Combine any sources. Cost tiered: website 5 · social 10/each ·
              doc 3/each · capped at {COST_CAP} credits.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Website */}
            <div>
              <label className="text-sm font-medium mb-1 block">Brand website</label>
              <Input
                type="url"
                placeholder="https://viriya.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                disabled={running}
                data-testid="brand-extract-website"
              />
            </div>

            {/* Social URLs */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">Social pages</label>
                {socialUrls.length < MAX_SOCIALS && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={handleAddSocial}
                    disabled={running}
                    data-testid="brand-extract-add-social"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {socialUrls.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="Instagram / YouTube / LinkedIn / X …"
                      value={s}
                      onChange={(e) => handleSocialChange(i, e.target.value)}
                      disabled={running}
                      data-testid={`brand-extract-social-${i}`}
                    />
                    {socialUrls.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveSocial(i)}
                        disabled={running}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Text */}
            <div>
              <label className="text-sm font-medium mb-1 block">Text description</label>
              <Textarea
                rows={3}
                placeholder="Anything you want us to know: values, positioning, voice, competitors, product lines…"
                value={textDescription}
                onChange={(e) => setTextDescription(e.target.value)}
                disabled={running}
                maxLength={20000}
                data-testid="brand-extract-text"
              />
              <div className="text-xs text-gray-400 mt-1">
                {textDescription.length} / 20000
              </div>
            </div>

            {/* Documents */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Documents (PDF, DOCX, PPT, TXT, MD, CSV, RTF — max 10 · 10 MB each)
              </label>
              <input
                type="file"
                multiple
                accept={ALLOWED_DOC_EXT.join(",")}
                onChange={handleFileUpload}
                disabled={running || docs.length >= 10}
                className="text-sm"
                data-testid="brand-extract-file-input"
              />
              {docs.length > 0 && (
                <div className="mt-2 space-y-1">
                  {docs.map((d, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-xs"
                      data-testid={`brand-extract-doc-${i}`}
                    >
                      <span className="truncate flex-1">
                        {d.name}{" "}
                        <span className="text-gray-400">
                          ({Math.round(d.size_bytes / 1024)} KB)
                        </span>
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeDoc(i)}
                        disabled={running}
                        className="text-red-500 h-6 px-2"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cost + status */}
            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
              <div className="text-sm">
                <span className="text-gray-600">Estimated cost:</span>{" "}
                <span className="font-semibold text-indigo-700">
                  {estCost} credits
                </span>
                {estCost === COST_CAP && (
                  <span className="text-xs text-gray-500 ml-2">(capped)</span>
                )}
              </div>
              <div className="text-xs text-gray-500">
                {hasWebsite && "🌐 website "}
                {socialCount > 0 && `📱 ${socialCount} social `}
                {docCount > 0 && `📄 ${docCount} doc${docCount > 1 ? "s" : ""} `}
                {textDescription.trim() && "📝 text"}
              </div>
            </div>

            {errorMsg && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="brand-extract-error">
                {errorMsg}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setWizardOpen(false);
                  setErrorMsg(null);
                }}
                disabled={running}
              >
                Cancel
              </Button>
              <Button
                onClick={handleStart}
                disabled={running || !anySource}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="brand-extract-start-btn"
              >
                {running
                  ? jobStatus === "processing"
                    ? "Extracting…"
                    : "Submitting…"
                  : `Extract (${estCost} credits)`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Extracted profile view ── */}
      {hasProfile ? (
        <BrandProfileView profile={profile} lastExtractedAt={brand.last_extracted_at} />
      ) : !wizardOpen ? (
        <PlaceholderCard
          title="No brand profile yet"
          message="Combine any of: brand website, social URLs, a text description, or uploaded documents. Our extractor builds identity, business info, tone of voice, USPs, and keywords."
          cta="Click Extract Brand Info above to begin"
        />
      ) : null}
    </div>
  );
};

/**
 * Render extracted profile. Admin's response shape is fluid, so we
 * render whatever nested keys we find — no schema lock-in.
 */
const BrandProfileView = ({ profile, lastExtractedAt }) => {
  return (
    <div className="space-y-3" data-testid="brand-profile-view">
      {lastExtractedAt && (
        <p className="text-xs text-gray-500">
          Last extracted: {new Date(lastExtractedAt).toLocaleString()}
        </p>
      )}
      {Object.entries(profile).map(([sectionKey, sectionValue]) => (
        <Card key={sectionKey}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm capitalize">
              {sectionKey.replace(/_/g, " ")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RenderValue value={sectionValue} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const RenderValue = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="text-xs text-gray-400 italic">—</span>;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return <span className="text-sm text-gray-700 whitespace-pre-wrap">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-xs text-gray-400 italic">—</span>;
    const allScalars = value.every((v) => typeof v !== "object" || v === null);
    if (allScalars) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v, i) => (
            <span
              key={i}
              className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs"
            >
              {String(v)}
            </span>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className="pl-3 border-l-2 border-gray-200">
            <RenderValue value={v} />
          </div>
        ))}
      </div>
    );
  }
  // object
  return (
    <div className="space-y-1.5">
      {Object.entries(value).map(([k, v]) => (
        <div key={k} className="text-sm">
          <span className="font-medium text-gray-600 capitalize">
            {k.replace(/_/g, " ")}:
          </span>{" "}
          <RenderValue value={v} />
        </div>
      ))}
    </div>
  );
};


// ─── Brand Assets tab (Phase 3, minimal) ───────────────────────────────
// Renders whatever assets URLs the brand has (as strings or {url, name}
// objects). GCS auto-download is future work — for now we display admin's
// returned URLs directly. Empty state prompts to run extraction.

const BrandAssetsTab = ({ brand, onBrandUpdate }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState("image");
  const [addName, setAddName] = useState("");
  const [addUrl, setAddUrl] = useState("");
  const [addFile, setAddFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const assets = Array.isArray(brand?.assets) ? brand.assets : [];

  const refreshBrand = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/brands/${brand.id}`, {
        withCredentials: true,
      });
      if (onBrandUpdate) onBrandUpdate(r.data);
    } catch (_) { /* ignore */ }
  };

  const handleAdd = async () => {
    if (!addUrl.trim() && !addFile) {
      toast.error("Provide either a URL or a file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", addKind || "image");
      if (addName.trim()) fd.append("name", addName.trim());
      if (addFile) fd.append("file", addFile);
      else fd.append("url", addUrl.trim());
      await axios.post(`${BACKEND_URL}/api/brands/${brand.id}/assets`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Asset added");
      setAddOpen(false);
      setAddName("");
      setAddUrl("");
      setAddFile(null);
      await refreshBrand();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add asset");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm("Delete this asset?")) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/brands/${brand.id}/assets/${assetId}`,
        { withCredentials: true }
      );
      toast.success("Asset deleted");
      await refreshBrand();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete asset");
    }
  };

  return (
    <div className="space-y-4" data-testid="brand-assets-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {assets.length === 0
            ? "Extract this brand or add assets (logos, hero images, product shots) manually."
            : `${assets.length} asset${assets.length === 1 ? "" : "s"}`}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAddOpen(true)}
          data-testid="asset-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add asset
        </Button>
      </div>

      {assets.length === 0 ? (
        <PlaceholderCard
          title="No brand assets yet"
          message='Extract on the Profile tab to auto-detect logos/hero images, or click "Add asset" to upload a file or paste a URL.'
          cta="Assets are used across every LoopAi app"
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="brand-assets-grid">
          {assets.map((asset, i) => {
            const isObj = typeof asset === "object" && asset !== null;
            const url = isObj ? asset.url || asset.image_url : asset;
            const name = isObj ? asset.name || asset.type || asset.kind : null;
            const id = isObj ? asset.id : null;
            const source = isObj ? asset.source : null;
            if (!url) return null;
            return (
              <div
                key={id || i}
                className="group relative rounded-lg overflow-hidden border-2 border-gray-200 hover:border-indigo-400 transition-all"
                data-testid={`brand-asset-${id || i}`}
              >
                <a href={url} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={url}
                      alt={name || `Asset ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2 text-xs bg-white flex items-center justify-between gap-1">
                    <span className="truncate text-gray-700">{name || asset.kind || "asset"}</span>
                    {source === "manual" && (
                      <span className="text-[9px] px-1 rounded bg-green-100 text-green-700 flex-shrink-0">manual</span>
                    )}
                  </div>
                </a>
                {id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-1 right-1 h-7 w-7 p-0 bg-white/90 hover:bg-red-50 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDelete(id);
                    }}
                    data-testid={`asset-delete-${id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add brand asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Kind</label>
              <select
                value={addKind}
                onChange={(e) => setAddKind(e.target.value)}
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm"
                data-testid="asset-add-kind"
              >
                <option value="logo">Logo</option>
                <option value="hero">Hero image</option>
                <option value="product">Product shot</option>
                <option value="image">Other image</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Name (optional)</label>
              <Input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. Primary logo (dark)"
                data-testid="asset-add-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Upload file</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setAddFile(e.target.files?.[0] || null)}
                className="text-sm block w-full"
                data-testid="asset-add-file"
              />
              <p className="text-[11px] text-gray-500 mt-1">or paste a URL below (max 10 MB)</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Image URL</label>
              <Input
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                placeholder="https://..."
                disabled={!!addFile}
                data-testid="asset-add-url"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={uploading || (!addFile && !addUrl.trim())}
              data-testid="asset-add-submit"
            >
              {uploading ? "Adding…" : "Add asset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Brand Products & Services tab (shared component) ───────────────────
// One component powers both tabs — the API shapes are near-identical, only
// the endpoint path + labels differ.

const BrandChildrenTab = ({ brand, kind, singular, Icon }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    description: "",
    category: "",
    features: "",
    keywords: "",
  });
  const [addImages, setAddImages] = useState([]);  // File[]
  const [adding, setAdding] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Detail-view image gallery state
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryFileInputRef = useRef(null);

  // Product sheet upload state (per ratio: "16:9" | "9:16")
  const [sheetUploading, setSheetUploading] = useState({}); // { "16:9": bool, "9:16": bool }
  const [sheetGenerating, setSheetGenerating] = useState({}); // { "16:9": bool, "9:16": bool }
  const sheetFileInputRef16x9 = useRef(null);
  const sheetFileInputRef9x16 = useRef(null);
  const sheetPollTimersRef = useRef({}); // { "16:9": timeout_id, "9:16": timeout_id }

  const base = `${BACKEND_URL}/api/brands/${brand.id}/${kind}`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(base, { withCredentials: true });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      console.error(`Failed to load ${kind}:`, err);
      toast.error(`Failed to load ${kind}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [brand.id, kind]);

  // Cancel any in-flight product-sheet polls when component unmounts.
  useEffect(() => {
    return () => {
      Object.values(sheetPollTimersRef.current || {}).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  const resetForm = () => {
    setAddForm({ name: "", description: "", category: "", features: "", keywords: "" });
    setAddImages([]);
  };

  const _splitList = (v) =>
    (v || "")
      .split(/[\n,]/)
      .map((x) => x.trim())
      .filter(Boolean);

  const handleAdd = async () => {
    const name = addForm.name.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    setAdding(true);
    try {
      // 1) Upload images (if any) via /brands/{id}/upload-image, collect URLs
      const uploadedUrls = [];
      for (const f of addImages) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const up = await axios.post(
            `${BACKEND_URL}/api/brands/${brand.id}/upload-image`,
            fd,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          if (up.data?.url) uploadedUrls.push(up.data.url);
        } catch (upErr) {
          toast.error(`Failed to upload ${f.name}: ${upErr.response?.data?.detail || upErr.message}`);
        }
      }

      // 2) Create the product / service
      const payload = {
        name,
        description: addForm.description.trim() || undefined,
        category: addForm.category.trim() || undefined,
        features: _splitList(addForm.features).length ? _splitList(addForm.features) : undefined,
        keywords: _splitList(addForm.keywords).length ? _splitList(addForm.keywords) : undefined,
        images: uploadedUrls.length ? uploadedUrls : undefined,
      };
      await axios.post(base, payload, { withCredentials: true });
      toast.success(`${singular[0].toUpperCase()}${singular.slice(1)} added`);
      setAddOpen(false);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to add ${singular}`);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${base}/${item.id}`, { withCredentials: true });
      toast.success(`${singular[0].toUpperCase()}${singular.slice(1)} deleted`);
      if (selected?.id === item.id) setSelected(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to delete ${singular}`);
    }
  };

  // ── Edit dialog ────────────────────────────────────────────────────────
  const openEdit = (item) => {
    // Convert list fields back to newline-joined strings for the textarea
    const listToStr = (v) => Array.isArray(v) ? v.filter(Boolean).join("\n") : (v || "");
    setEditItemId(item.id);
    setEditForm({
      name: item.name || "",
      description: item.description || "",
      short_description: item.short_description || "",
      long_description: item.long_description || "",
      marketing_description: item.marketing_description || "",
      category: item.category || "",
      subcategory: item.subcategory || "",
      sku: item.sku || "",
      product_type: item.product_type || "",
      features: listToStr(item.features),
      benefits: listToStr(item.benefits),
      usps: listToStr(item.usps),
      keywords: listToStr(item.keywords),
      // service-only
      process: listToStr(item.process),
      deliverables: listToStr(item.deliverables),
      use_cases: listToStr(item.use_cases),
      pricing: item.pricing || "",
      target_customers: item.target_customers || "",
      // specifications kept as raw JSON string for now
      specifications:
        item.specifications && typeof item.specifications === "object" && Object.keys(item.specifications).length
          ? JSON.stringify(item.specifications, null, 2)
          : "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const name = (editForm.name || "").trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    setEditSaving(true);
    try {
      const payload = { name };
      const strFields = ["description", "short_description", "long_description",
        "marketing_description", "category", "subcategory", "sku", "product_type",
        "pricing", "target_customers"];
      for (const f of strFields) {
        if (editForm[f] !== undefined) payload[f] = (editForm[f] || "").trim() || null;
      }
      const listFields = ["features", "benefits", "usps", "keywords",
        "process", "deliverables", "use_cases"];
      for (const f of listFields) {
        payload[f] = _splitList(editForm[f]);
      }
      if (editForm.specifications !== undefined) {
        const raw = (editForm.specifications || "").trim();
        if (!raw) {
          payload.specifications = {};
        } else {
          try {
            const parsed = JSON.parse(raw);
            if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
              payload.specifications = parsed;
            } else {
              throw new Error("must be a JSON object");
            }
          } catch (err) {
            toast.error(`Specifications must be valid JSON object: ${err.message}`);
            setEditSaving(false);
            return;
          }
        }
      }
      const r = await axios.put(`${base}/${editItemId}`, payload, {
        withCredentials: true,
      });
      toast.success(`${singular[0].toUpperCase()}${singular.slice(1)} updated`);
      setEditOpen(false);
      // Update the selected + list without a full reload
      if (r.data) {
        setSelected(r.data);
        setItems((prev) => prev.map((x) => (x.id === editItemId ? r.data : x)));
      } else {
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save changes");
    } finally {
      setEditSaving(false);
    }
  };

  // ── Detail-view image gallery ──────────────────────────────────────────
  const _imgUrl = (img) => (typeof img === "string" ? img : img?.url);

  const uploadGalleryImages = async (files) => {
    if (!selected || !files || files.length === 0) return;
    setGalleryUploading(true);
    try {
      const newUrls = [];
      for (const f of Array.from(files)) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const up = await axios.post(
            `${BACKEND_URL}/api/brands/${brand.id}/upload-image`,
            fd,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          if (up.data?.url) newUrls.push(up.data.url);
        } catch (upErr) {
          toast.error(`Failed to upload ${f.name}: ${upErr.response?.data?.detail || upErr.message}`);
        }
      }
      if (newUrls.length === 0) return;
      const existing = Array.isArray(selected.images) ? selected.images : [];
      const combined = [...existing, ...newUrls];
      const r = await axios.put(
        `${base}/${selected.id}`,
        { images: combined },
        { withCredentials: true }
      );
      toast.success(`${newUrls.length} image${newUrls.length === 1 ? "" : "s"} added`);
      if (r.data) {
        setSelected(r.data);
        setItems((prev) => prev.map((x) => (x.id === selected.id ? r.data : x)));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach images");
    } finally {
      setGalleryUploading(false);
      if (galleryFileInputRef.current) galleryFileInputRef.current.value = "";
    }
  };

  const removeGalleryImage = async (index) => {
    if (!selected) return;
    const existing = Array.isArray(selected.images) ? selected.images : [];
    const remaining = existing.filter((_, i) => i !== index);
    try {
      const r = await axios.put(
        `${base}/${selected.id}`,
        { images: remaining },
        { withCredentials: true }
      );
      toast.success("Image removed");
      if (r.data) {
        setSelected(r.data);
        setItems((prev) => prev.map((x) => (x.id === selected.id ? r.data : x)));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove image");
    }
  };

  // ── Product sheet upload / delete ─────────────────────────────────────
  const uploadProductSheet = async (ratio, file) => {
    if (!selected || !file) return;
    setSheetUploading((prev) => ({ ...prev, [ratio]: true }));
    try {
      const fd = new FormData();
      fd.append("ratio", ratio);
      fd.append("file", file);
      const r = await axios.post(
        `${BACKEND_URL}/api/brands/${brand.id}/products/${selected.id}/sheet`,
        fd,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (r.data?.success && r.data?.url) {
        const field = ratio === "16:9" ? "product_sheet_16_9" : "product_sheet_9_16";
        const updated = { ...selected, [field]: r.data.url };
        setSelected(updated);
        setItems((prev) => prev.map((x) => (x.id === selected.id ? updated : x)));
        toast.success(`${ratio} product sheet uploaded`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${ratio} sheet`);
    } finally {
      setSheetUploading((prev) => ({ ...prev, [ratio]: false }));
      const ref = ratio === "16:9" ? sheetFileInputRef16x9 : sheetFileInputRef9x16;
      if (ref.current) ref.current.value = "";
    }
  };

  const deleteProductSheet = async (ratio) => {
    if (!selected) return;
    if (!window.confirm(`Delete ${ratio} product sheet?`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/brands/${brand.id}/products/${selected.id}/sheet`,
        {
          params: { ratio },
          withCredentials: true,
        }
      );
      const field = ratio === "16:9" ? "product_sheet_16_9" : "product_sheet_9_16";
      const updated = { ...selected };
      delete updated[field];
      setSelected(updated);
      setItems((prev) => prev.map((x) => (x.id === selected.id ? updated : x)));
      toast.success(`${ratio} product sheet removed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to delete ${ratio} sheet`);
    }
  };

  const generateProductSheet = async (ratio) => {
    if (!selected) return;
    const hasImages = Array.isArray(selected.images) && selected.images.length > 0;
    if (!hasImages) {
      toast.error("Upload at least 1 product image before generating an AI sheet.");
      return;
    }
    setSheetGenerating((prev) => ({ ...prev, [ratio]: true }));
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/brands/${brand.id}/products/${selected.id}/sheet/generate`,
        { ratio },
        { withCredentials: true }
      );
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      toast.success(`Generating ${ratio} sheet — this usually takes 60-90s.`);
      pollSheetJob(ratio, jobId);
    } catch (err) {
      setSheetGenerating((prev) => ({ ...prev, [ratio]: false }));
      toast.error(err.response?.data?.detail || `Failed to start generation`);
    }
  };

  const pollSheetJob = (ratio, jobId) => {
    let attempts = 0;
    const maxAttempts = 60; // ~180s total at 3s interval
    const tick = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/ai/jobs/${jobId}`, {
          withCredentials: true,
        });
        const status = r.data?.status;
        if (status === "completed") {
          const url = r.data?.result?.url;
          const field = ratio === "16:9" ? "product_sheet_16_9" : "product_sheet_9_16";
          if (url) {
            const updated = { ...selected, [field]: url };
            setSelected(updated);
            setItems((prev) => prev.map((x) => (x.id === selected.id ? updated : x)));
            toast.success(`${ratio} product sheet ready!`);
          } else {
            toast.error(`Generation finished but no image URL returned`);
          }
          setSheetGenerating((prev) => ({ ...prev, [ratio]: false }));
          return;
        }
        if (status === "failed") {
          toast.error(r.data?.error || `${ratio} generation failed`);
          setSheetGenerating((prev) => ({ ...prev, [ratio]: false }));
          return;
        }
        if (attempts >= maxAttempts) {
          toast.error(`${ratio} generation timed out. Please try again.`);
          setSheetGenerating((prev) => ({ ...prev, [ratio]: false }));
          return;
        }
        sheetPollTimersRef.current[ratio] = setTimeout(tick, 3000);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to check job status`);
        setSheetGenerating((prev) => ({ ...prev, [ratio]: false }));
      }
    };
    tick();
  };

  const selectedView = selected ? (
      <div className="space-y-4" data-testid={`${singular}-detail-view`}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {kind}
          </Button>
        </div>
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle>{selected.name}</CardTitle>
                  {selected.category && (
                    <p className="text-xs text-gray-500 mt-1">
                      {selected.category}
                      {selected.subcategory && ` › ${selected.subcategory}`}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(selected)}
                  data-testid={`${singular}-detail-edit`}
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(selected)}
                  className="text-red-500 hover:bg-red-50"
                  data-testid={`${singular}-detail-delete`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Image gallery with placeholders + upload/delete */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Images
                  {Array.isArray(selected.images) && selected.images.length > 0 && (
                    <span className="text-gray-400 ml-1">({selected.images.length})</span>
                  )}
                </p>
                <input
                  ref={galleryFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadGalleryImages(e.target.files)}
                  data-testid={`${singular}-gallery-file-input`}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={galleryUploading}
                  onClick={() => galleryFileInputRef.current?.click()}
                  data-testid={`${singular}-gallery-upload-btn`}
                >
                  {galleryUploading ? "Uploading…" : (
                    <>
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      Upload images
                    </>
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {(Array.isArray(selected.images) ? selected.images : []).map((img, i) => {
                  const u = _imgUrl(img);
                  if (!u) return null;
                  return (
                    <div
                      key={i}
                      className="group relative rounded-md overflow-hidden border-2 border-gray-200 hover:border-indigo-400 transition-all"
                      data-testid={`${singular}-gallery-image-${i}`}
                    >
                      <a href={u} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={u} alt="" className="w-full h-24 object-cover" loading="lazy" />
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-1 right-1 h-6 w-6 p-0 bg-white/90 hover:bg-red-50 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeGalleryImage(i); }}
                        data-testid={`${singular}-gallery-remove-${i}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
                {/* Placeholder upload tile — shown when < 8 images */}
                {(!Array.isArray(selected.images) || selected.images.length < 8) && (
                  <button
                    type="button"
                    disabled={galleryUploading}
                    onClick={() => galleryFileInputRef.current?.click()}
                    className="h-24 w-full rounded-md border-2 border-dashed border-gray-300 hover:border-indigo-400 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 flex flex-col items-center justify-center gap-1 transition-all disabled:opacity-50"
                    data-testid={`${singular}-gallery-placeholder`}
                  >
                    <ImagePlus className="w-5 h-5 text-indigo-400" />
                    <span className="text-[11px] text-gray-500">
                      {(Array.isArray(selected.images) && selected.images.length > 0)
                        ? "Add more"
                        : "Add image"}
                    </span>
                  </button>
                )}
              </div>
              {(!Array.isArray(selected.images) || selected.images.length === 0) && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Tap the tile to upload a product image. JPG/PNG/WebP, max 10 MB each.
                </p>
              )}
            </div>

            {/* Product Sheet (16:9 + 9:16) — products only */}
            {singular === "product" && (
              <div className="mb-5" data-testid="product-sheet-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Product Sheet</p>
                  <span className="text-[11px] text-gray-400">
                    Upload branded sheets for downstream apps (Studio Pro, Ad Creator, Movie Studio).
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { ratio: "16:9", field: "product_sheet_16_9", label: "Landscape 16:9", ref: sheetFileInputRef16x9, aspect: "aspect-video" },
                    { ratio: "9:16", field: "product_sheet_9_16", label: "Portrait 9:16", ref: sheetFileInputRef9x16, aspect: "aspect-[9/16]" },
                  ].map((cfg) => {
                    const url = selected[cfg.field];
                    const busy = !!sheetUploading[cfg.ratio];
                    const generating = !!sheetGenerating[cfg.ratio];
                    const hasImgs = Array.isArray(selected.images) && selected.images.length > 0;
                    return (
                      <div
                        key={cfg.ratio}
                        className="rounded-md border border-gray-200 p-2 bg-white"
                        data-testid={`product-sheet-card-${cfg.ratio.replace(":", "x")}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-600">{cfg.label}</p>
                          {url && !generating && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                              onClick={() => deleteProductSheet(cfg.ratio)}
                              data-testid={`product-sheet-delete-${cfg.ratio.replace(":", "x")}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <input
                          ref={cfg.ref}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadProductSheet(cfg.ratio, f);
                          }}
                          data-testid={`product-sheet-input-${cfg.ratio.replace(":", "x")}`}
                        />
                        {generating ? (
                          <div className={`${cfg.aspect} rounded overflow-hidden border-2 border-dashed border-indigo-400 bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col items-center justify-center gap-2 p-3`}>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                            <span className="text-[11px] text-indigo-700 font-medium">Generating {cfg.ratio}…</span>
                            <span className="text-[10px] text-gray-500 text-center">Using your product images as reference. ~60-90s.</span>
                          </div>
                        ) : url ? (
                          <div className={`${cfg.aspect} rounded overflow-hidden border border-gray-100 group relative`}>
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={url}
                                alt={`${cfg.label} product sheet`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </a>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => cfg.ref.current?.click()}
                              className="absolute inset-0 bg-black/0 hover:bg-black/50 text-white text-xs font-medium flex items-center justify-center opacity-0 hover:opacity-100 transition"
                              data-testid={`product-sheet-replace-${cfg.ratio.replace(":", "x")}`}
                            >
                              {busy ? "Uploading…" : "Replace"}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => cfg.ref.current?.click()}
                            className={`${cfg.aspect} w-full rounded border-2 border-dashed border-gray-300 hover:border-indigo-400 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 flex flex-col items-center justify-center gap-1 transition-all disabled:opacity-50`}
                            data-testid={`product-sheet-upload-${cfg.ratio.replace(":", "x")}`}
                          >
                            {busy ? (
                              <span className="text-xs text-gray-500">Uploading…</span>
                            ) : (
                              <>
                                <ImagePlus className="w-5 h-5 text-indigo-400" />
                                <span className="text-[11px] text-gray-500">Upload {cfg.ratio}</span>
                              </>
                            )}
                          </button>
                        )}

                        {/* Generate with AI action */}
                        {!generating && (
                          <button
                            type="button"
                            onClick={() => generateProductSheet(cfg.ratio)}
                            disabled={!hasImgs || busy}
                            title={hasImgs ? `Generate ${cfg.ratio} sheet using product images as reference` : "Upload a product image first"}
                            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[11px] font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            data-testid={`product-sheet-generate-${cfg.ratio.replace(":", "x")}`}
                          >
                            <Sparkles className="w-3 h-3" />
                            {url ? `Regenerate with AI` : `Generate with AI`}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <RenderValue
              value={Object.fromEntries(
                Object.entries(selected).filter(([k]) => ![
                  "_id", "id", "brand_id", "user_id", "name", "images",
                  "created_at", "updated_at", "source",
                  "product_sheet_16_9", "product_sheet_9_16",
                ].includes(k))
              )}
            />
          </CardContent>
        </Card>
      </div>
    ) : null;

  return (
    <>
    {selectedView}
    {!selected && (
    <div className="space-y-4" data-testid={`${singular}-list-view`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {items.length === 0
            ? `Extract this brand to populate ${kind}, or add ${singular}s manually.`
            : `${items.length} ${kind}`}
        </p>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
          variant="outline"
          data-testid={`${singular}-add-btn`}
        >
          <Plus className="w-4 h-4 mr-1" />
          Add {singular}
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center text-gray-500">Loading…</CardContent></Card>
      ) : items.length === 0 ? (
        <PlaceholderCard
          title={`No ${kind} yet`}
          message={
            singular === "product"
              ? "Products get their own Profile — name, description, features, specs, images, and AI-friendly keywords."
              : "Services get their own Profile — description, process, deliverables, target customers."
          }
          cta={`Run extraction (Profile tab) or click "Add ${singular}"`}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => {
            const firstImg =
              Array.isArray(item.images) && item.images.length > 0
                ? (typeof item.images[0] === "string" ? item.images[0] : item.images[0]?.url)
                : null;
            return (
              <Card
                key={item.id}
                className="border-2 hover:border-indigo-400 hover:shadow-lg transition-all cursor-pointer overflow-hidden"
                onClick={() => setSelected(item)}
                data-testid={`${singular}-card-${item.id}`}
              >
                {firstImg ? (
                  <div className="aspect-video bg-gray-100 overflow-hidden">
                    <img src={firstImg} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center border-b">
                    <Icon className="w-10 h-10 text-indigo-300" strokeWidth={1.5} />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Icon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                      <CardTitle className="text-base truncate">{item.name}</CardTitle>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:bg-red-50 flex-shrink-0 h-7 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {item.category && (
                    <p className="text-xs text-gray-500">
                      {item.category}
                      {item.subcategory && ` › ${item.subcategory}`}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem]">
                    {item.short_description || item.description || (
                      <span className="italic text-gray-400">No description</span>
                    )}
                  </p>
                  {item.source === "extraction" && (
                    <span className="inline-block mt-2 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium">
                      From extraction
                    </span>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    )}

      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add {singular}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder={singular === "product" ? "e.g. Rosemary Hair Oil" : "e.g. Ayurvedic Consultation"}
                autoFocus
                data-testid={`${singular}-add-name`}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                rows={3}
                placeholder={
                  singular === "product"
                    ? "What is it? What does it do?"
                    : "What does the service include?"
                }
                data-testid={`${singular}-add-description`}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                {singular === "product" ? "Category" : "Category / offering type"}
              </label>
              <Input
                value={addForm.category}
                onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
                placeholder={singular === "product" ? "Haircare / Skincare / …" : "Consultation / Workshop / …"}
                data-testid={`${singular}-add-category`}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Features (comma or newline separated)
              </label>
              <Textarea
                value={addForm.features}
                onChange={(e) => setAddForm({ ...addForm, features: e.target.value })}
                rows={2}
                placeholder="Cold-pressed, Vegan, Cruelty-free"
                data-testid={`${singular}-add-features`}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Keywords (SEO / AI hints)</label>
              <Input
                value={addForm.keywords}
                onChange={(e) => setAddForm({ ...addForm, keywords: e.target.value })}
                placeholder="hair, natural, wellness"
                data-testid={`${singular}-add-keywords`}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Images (optional, up to 6)
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setAddImages(Array.from(e.target.files || []).slice(0, 6))}
                className="text-sm block w-full"
                data-testid={`${singular}-add-images`}
              />
              {addImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {addImages.map((f, i) => (
                    <span key={i} className="text-[11px] px-1.5 py-0.5 bg-gray-100 rounded truncate max-w-[140px]">
                      {f.name}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-1">
                A placeholder icon shows if no image is uploaded. Max 10 MB each.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={adding || !addForm.name.trim()} data-testid={`${singular}-add-submit`}>
              {adding ? "Adding…" : `Add ${singular}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit dialog ────────────────────────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditItemId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {singular}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid={`${singular}-edit-name`}
              />
            </div>
            {singular === "product" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Category</label>
                  <Input
                    value={editForm.category || ""}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    data-testid={`${singular}-edit-category`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Subcategory</label>
                  <Input
                    value={editForm.subcategory || ""}
                    onChange={(e) => setEditForm({ ...editForm, subcategory: e.target.value })}
                    data-testid={`${singular}-edit-subcategory`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">SKU</label>
                  <Input
                    value={editForm.sku || ""}
                    onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })}
                    data-testid={`${singular}-edit-sku`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Product type</label>
                  <select
                    value={editForm.product_type || ""}
                    onChange={(e) => setEditForm({ ...editForm, product_type: e.target.value })}
                    className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm"
                    data-testid={`${singular}-edit-product-type`}
                  >
                    <option value="">—</option>
                    <option value="physical">physical</option>
                    <option value="digital">digital</option>
                    <option value="subscription">subscription</option>
                    <option value="service">service</option>
                  </select>
                </div>
              </div>
            )}
            {singular === "service" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Pricing</label>
                  <Input
                    value={editForm.pricing || ""}
                    onChange={(e) => setEditForm({ ...editForm, pricing: e.target.value })}
                    placeholder="e.g. ₹1,499 / hour"
                    data-testid={`${singular}-edit-pricing`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Target customers</label>
                  <Input
                    value={editForm.target_customers || ""}
                    onChange={(e) => setEditForm({ ...editForm, target_customers: e.target.value })}
                    data-testid={`${singular}-edit-target-customers`}
                  />
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                rows={3}
                value={editForm.description || ""}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                data-testid={`${singular}-edit-description`}
              />
            </div>
            {singular === "product" && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">Short description</label>
                  <Textarea
                    rows={2}
                    value={editForm.short_description || ""}
                    onChange={(e) => setEditForm({ ...editForm, short_description: e.target.value })}
                    data-testid={`${singular}-edit-short-description`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Long description</label>
                  <Textarea
                    rows={3}
                    value={editForm.long_description || ""}
                    onChange={(e) => setEditForm({ ...editForm, long_description: e.target.value })}
                    data-testid={`${singular}-edit-long-description`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Marketing description</label>
                  <Textarea
                    rows={2}
                    value={editForm.marketing_description || ""}
                    onChange={(e) => setEditForm({ ...editForm, marketing_description: e.target.value })}
                    data-testid={`${singular}-edit-marketing-description`}
                  />
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Features (one per line, or comma separated)</label>
              <Textarea
                rows={2}
                value={editForm.features || ""}
                onChange={(e) => setEditForm({ ...editForm, features: e.target.value })}
                data-testid={`${singular}-edit-features`}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Benefits</label>
              <Textarea
                rows={2}
                value={editForm.benefits || ""}
                onChange={(e) => setEditForm({ ...editForm, benefits: e.target.value })}
                data-testid={`${singular}-edit-benefits`}
              />
            </div>
            {singular === "product" && (
              <div>
                <label className="text-sm font-medium mb-1 block">USPs (unique selling points)</label>
                <Textarea
                  rows={2}
                  value={editForm.usps || ""}
                  onChange={(e) => setEditForm({ ...editForm, usps: e.target.value })}
                  data-testid={`${singular}-edit-usps`}
                />
              </div>
            )}
            {singular === "service" && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">Process steps</label>
                  <Textarea
                    rows={2}
                    value={editForm.process || ""}
                    onChange={(e) => setEditForm({ ...editForm, process: e.target.value })}
                    data-testid={`${singular}-edit-process`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Deliverables</label>
                  <Textarea
                    rows={2}
                    value={editForm.deliverables || ""}
                    onChange={(e) => setEditForm({ ...editForm, deliverables: e.target.value })}
                    data-testid={`${singular}-edit-deliverables`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Use cases</label>
                  <Textarea
                    rows={2}
                    value={editForm.use_cases || ""}
                    onChange={(e) => setEditForm({ ...editForm, use_cases: e.target.value })}
                    data-testid={`${singular}-edit-use-cases`}
                  />
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Keywords</label>
              <Input
                value={editForm.keywords || ""}
                onChange={(e) => setEditForm({ ...editForm, keywords: e.target.value })}
                placeholder="natural, hair, wellness"
                data-testid={`${singular}-edit-keywords`}
              />
            </div>
            {singular === "product" && (
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Specifications (JSON object)
                </label>
                <Textarea
                  rows={4}
                  value={editForm.specifications || ""}
                  onChange={(e) => setEditForm({ ...editForm, specifications: e.target.value })}
                  placeholder='{"volume": "100 ml", "shelf_life": "24 months"}'
                  className="font-mono text-xs"
                  data-testid={`${singular}-edit-specifications`}
                />
                <p className="text-[11px] text-gray-500 mt-1">Leave blank to clear. Must be valid JSON.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editSaving || !(editForm.name || "").trim()}
              data-testid={`${singular}-edit-submit`}
            >
              {editSaving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

